import { useState, type CSSProperties, type MouseEvent } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { Node, NodeProps } from '@xyflow/react';
import { useTimelineStore } from './timeline-store';
import { useAnnotationSource } from '../annotations/hooks/use-annotation-source';

export const NODE_COLORS = {
  activeUser: {
    background: '#fafafa',
    border: '#e5e5e5',
    text: '#1a1a1a',
    accent: '#d97757',
  },
  activeAssistant: {
    background: '#ffffff',
    border: '#e8e8e8',
    text: '#2d2d2d',
    accent: '#a8a29e',
  },
  inactive: {
    background: '#f8f8f8',
    border: '#ebebeb',
    text: '#9ca3af',
    accent: '#d4d4d4',
  },
  edge: {
    active: '#d4d4d4',
    inactive: '#e5e5e5',
  },
} as const;

export const NODE_DIMENSIONS = {
  width: 200,
  paddingX: 16,
  paddingY: 12,
  borderRadius: 10,
  borderWidth: 1,
  accentWidth: 3,
} as const;

export interface MessageNodeData extends Record<string, unknown> {
  uuid: string;
  fullText: string;
  preview: string;
  tooltipPreview: string;
  sender: 'human' | 'assistant';
  isOnActivePath: boolean;
}

export type MessageNodeType = Node<MessageNodeData, 'message'>;

const handleStyle: CSSProperties = {
  width: 6,
  height: 6,
  background: '#e5e5e5',
  border: 'none',
  opacity: 0,
};

export default function MessageNode({
  data,
}: Readonly<NodeProps<MessageNodeType>>): JSX.Element {
  const [isHovered, setIsHovered] = useState(false);
  const conversationId = useTimelineStore((state) => state.conversationId);
  const conversationName = useTimelineStore((state) => state.conversationName);
  const activePlatform = useTimelineStore((state) => state.activePlatform);
  const {
    addAnnotation,
    removeAnnotationByMessageId,
    getAnnotationByMessageId,
  } = useAnnotationSource();
  const hasTooltip = data.tooltipPreview.length > 0 && data.tooltipPreview !== data.preview;
  const showTooltip = isHovered && hasTooltip;
  const isBookmarked = Boolean(getAnnotationByMessageId(data.uuid));

  const colors = !data.isOnActivePath
    ? NODE_COLORS.inactive
    : data.sender === 'human'
      ? NODE_COLORS.activeUser
      : NODE_COLORS.activeAssistant;

  const handleBookmarkClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (isBookmarked) {
      await removeAnnotationByMessageId(data.uuid);
      return;
    }

    if (!conversationId || !conversationName) {
      return;
    }

    await addAnnotation({
      conversationId,
      conversationName,
      messageId: data.uuid,
      messageText: data.fullText,
      ...(activePlatform ? { platform: activePlatform } : {}),
    });
  };

  return (
    <div
      style={{
        width: NODE_DIMENSIONS.width,
        padding: `${NODE_DIMENSIONS.paddingY}px ${NODE_DIMENSIONS.paddingX}px`,
        borderRadius: NODE_DIMENSIONS.borderRadius,
        backgroundColor: colors.background,
        border: `${NODE_DIMENSIONS.borderWidth}px solid ${colors.border}`,
        borderLeft: `${NODE_DIMENSIONS.accentWidth}px solid ${colors.accent}`,
        boxShadow: '0 1px 3px rgb(0 0 0 / 0.04), 0 1px 2px rgb(0 0 0 / 0.02)',
        color: colors.text,
        lineHeight: 1.5,
        wordWrap: 'break-word',
        position: 'relative',
        overflow: 'visible',
        fontSize: 14,
      }}
      data-on-active-path={data.isOnActivePath}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Handle type="target" position={Position.Top} style={handleStyle} />
      {data.sender === 'assistant' && isHovered ? (
        <button
          type="button"
          onClick={(event) => {
            void handleBookmarkClick(event);
          }}
          aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 22,
            height: 22,
            borderRadius: 6,
            border: '1px solid #e5e7eb',
            backgroundColor: '#ffffff',
            color: isBookmarked ? '#6b7280' : '#9ca3af',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 25,
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill={isBookmarked ? 'currentColor' : 'none'}
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M6 3H18C18.5523 3 19 3.44772 19 4V21L12 16L5 21V4C5 3.44772 5.44772 3 6 3Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : null}
      <div style={{ paddingRight: data.sender === 'assistant' ? 20 : 0 }}>
        {data.preview || '(empty message)'}
      </div>

      {showTooltip ? (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translate(-50%, -8px)',
            width: 320,
            maxWidth: 360,
            backgroundColor: '#111827',
            color: '#f9fafb',
            borderRadius: 8,
            padding: '8px 10px',
            fontSize: 12,
            lineHeight: 1.4,
            boxShadow: '0 10px 25px rgb(0 0 0 / 0.2)',
            zIndex: 20,
            whiteSpace: 'normal',
            pointerEvents: 'none',
          }}
        >
          {data.tooltipPreview}
        </div>
      ) : null}

      <Handle type="source" position={Position.Bottom} style={handleStyle} />
    </div>
  );
}
